import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmDialogList from '../../components/ConfirmDialogList';
import configSiteUrls from '../site/configs/configSiteUrls';
import { ISusuario, Usuario } from '../../types/usuario/Usuario';
import { UsuarioList } from '../../types/usuario/UsuarioList';
import { useChangePasswordUsuarioMutation, useDeleteUsuarioMutation, useGetUsuariosQuery } from './usuarioApiSlice';
import formatErrorMessage from '../../utils/errorFormat';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault'; // Importando o ícone adequado
import LockIcon from '@mui/icons-material/Lock';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { DialogSetores } from './DialogSetores';

export const UsuarioListForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [usuarioToDelete, setUsuarioToDelete] = useState<Usuario>(ISusuario);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteUsuario, { error: errorUsuario, isLoading, isSuccess }] = useDeleteUsuarioMutation();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const [changePasswdUsuario, status] = useChangePasswordUsuarioMutation();

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700);

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  const [usuarios, setUsuarios] = useState<UsuarioList[]>([]);
  const { data, isFetching, error } = useGetUsuariosQuery({
    search: debouncedSearch
  });

  useEffect(() => {
    if (data) {
      setUsuarios(data); // copia da API para o estado local
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Usuarios não encontrados!', { variant: 'error' });
      const erroReturn: string = formatErrorMessage(error);
      enqueueSnackbar(erroReturn, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [usuarios, debouncedSearch]); // Foca quando a busca é executada

  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      Registro(s) encontrado(s): {usuarios.length}
    </Typography>
  );

  const renderAdmSite = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.administradorSite ? 'Sim' : 'Não'}
      </Typography>
    ),
    []
  );

  const renderAdmOuvidoria = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.administradorOuvidoria ? 'Sim' : 'Não'}
      </Typography>
    ),
    []
  );

  const renderTipo = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.tipo || 'Tipo não disponível'}
      </Typography>
    ),
    []
  );

  const renderLogin = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.login || 'Login não disponível'}
      </Typography>
    ),
    []
  );

  const renderAtivo = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.ativo ? 'Sim' : 'Não'}
      </Typography>
    ),
    []
  );

  const renderNome = useCallback(
    (rowData: UsuarioList) => (
      <Typography color="secondary" variant="body2">
        {rowData.nome}
      </Typography>
    ),
    []
  );

  const renderAction = useCallback(
    (rowData: UsuarioList) => {
      return (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Editar">
            <Link to={`${configSiteUrls.SITE_USUARIO_EDIT_LK}${rowData.id}`}>
              <IconButton color="secondary" aria-label="edit">
                <EditIcon />
              </IconButton>
            </Link>
          </Tooltip>

          <Tooltip title={`${rowData.ativo ? 'Desativar' : 'Ativar'}`}>
            <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
              <DisabledByDefaultIcon sx={{ color: `${rowData.ativo ? 'none' : 'Orange'}` }} />
            </IconButton>
          </Tooltip>

          {/* Mudar Senha */}
          <Tooltip title="Mudar Senha">
            <IconButton color="secondary" aria-label="change-password" onClick={() => handleChangePasswordClick(rowData)}>
              <LockIcon sx={{ color: 'blue' }} /> {/* Ícone de mudar senha */}
            </IconButton>
          </Tooltip>

          {/* Setores */}
          <Tooltip title="Setores do Usuário">
            <IconButton color="secondary" aria-label="setores" onClick={() => handleSetoresClick(rowData)}>
              <ApartmentIcon sx={{ color: 'green' }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    [navigate]
  ); // Adicione navigate se usar hooks dentro

  const handleExcluirtClick = (rowData: UsuarioList) => {
    setUsuarioToDelete(rowData as Usuario);
    setOpenConfirmExcluir(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteUsuario({
      id: usuarioToDelete.id ? usuarioToDelete.id : ''
    });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Usuário excluído com sucesso!', { variant: 'success' });
    }
    if (errorUsuario) {
      enqueueSnackbar('Erro ao excluir o usuário!', { variant: 'error' });
    }
  }, [isSuccess, errorUsuario, enqueueSnackbar]);

  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);
  const [usuarioToChangePassword, setUsuarioToChangePassword] = useState<Usuario | null>(null);

  const handleChangePasswordClick = (rowData: UsuarioList) => {
    setUsuarioToChangePassword(rowData as Usuario); // Armazenar o usuário para saber qual mudar a senha
    setOpenChangePasswordDialog(true); // Abrir o modal
  };

  const handleCloseChangePasswordDialog = () => {
    setOpenChangePasswordDialog(false);
  };

  const handleChangePassword = async (newPassword: string, oldPassword: string) => {
    if (!usuarioToChangePassword) return;

    await changePasswdUsuario({ id: usuarioToChangePassword.id, newPassword, oldPassword });
    setOpenChangePasswordDialog(false); // Fechar o modal após sucesso
  };

  useEffect(() => {
    if (status.isError) {
      enqueueSnackbar('A senha não foi alterada!', { variant: 'error' });
      const erroReturn: string = formatErrorMessage(status);
      enqueueSnackbar(erroReturn, { variant: 'error' });
    }

    if (status.isSuccess) {
      enqueueSnackbar('Senha Alterada!', { variant: 'success' });
      navigate(-1);
      // setGrupo(ISgrupo);
    }
  }, [status]);

  const [openDialogSetores, setOpenDialogSetores] = useState(false);
  const [usuarioSelecionadoSetor, setUsuarioSelecionadoSetor] = useState<UsuarioList | null>(null);

  const handleSetoresClick = (usuario: UsuarioList) => {
    setUsuarioSelecionadoSetor(usuario);
    setOpenDialogSetores(true);
  };

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ChangePasswordDialog isAdminArea={true} open={openChangePasswordDialog} onClose={handleCloseChangePasswordDialog} onConfirm={handleChangePassword} usuario={usuarioToChangePassword} />

      {usuarioSelecionadoSetor && (
        <DialogSetores
          open={openDialogSetores}
          onClose={() => setOpenDialogSetores(false)}
          usuario={usuarioSelecionadoSetor}
          atualizarUsuarioNaLista={usuarioAtualizado => {
            setUsuarios(prev => prev.map(u => (u.id === usuarioAtualizado.id ? usuarioAtualizado : u)));
          }}
        />
      )}

      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={[`Você tem certeza de que deseja ${usuarioToDelete.ativo ? 'desativar' : 'ativar'} esse usuário?`, usuarioToDelete.nome ? `Usuário: ${usuarioToDelete.nome}` : '']}
        handleDelete={() => {
          handleConfirm();
          setOpenConfirmExcluir(false);
        }}
      />
      <Paper sx={{ width: '100%', height: '100vh', padding: '12px' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Cadastro de Usuários
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate(configSiteUrls.SITE_USUARIO_CREATE);
                    }}
                  >
                    Novo
                  </Button>

                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>

        <DataTable value={usuarios} paginator rows={25} showGridlines size="small" resizableColumns columnResizeMode="fit" dataKey="id" className="custom-datatable" scrollable scrollHeight="flex">
          <Column header="Ativo" body={renderAtivo} sortable style={{ width: '6rem' }} />
          <Column header="Nome" body={renderNome} sortable style={{ width: '28rem' }} />
          <Column header="Login" body={renderLogin} sortable style={{ width: '14rem' }} />
          <Column header="Tipo" body={renderTipo} sortable style={{ width: '10rem' }} />
          <Column header="Ouvidoria" body={renderAdmOuvidoria} sortable style={{ width: '8rem' }} />
          <Column header="Adm Site" body={renderAdmSite} sortable style={{ width: '8rem' }} />
          <Column header="Ações" body={renderAction} style={{ width: '10rem', textAlign: 'center' }} />
        </DataTable>
      </Paper>
    </>
  );
};
